/**
 * The player shell: name form, stored token, claim offer, join errors, the kicked
 * screen and the toast.
 * A game's player UI never touches a credential.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSession, type JoinParams } from '../lib/useSession';
import { clearPlayer, loadPlayer, savePlayer, type StoredPlayer } from '../lib/storage';
import { notePlayerChanged } from '../lib/whoami';
import { uiFor } from '../games';
import { Toast } from '../kit/Toast';
import { Interstitial } from '../show/Interstitial';
import { useRecordResult } from '../lib/results';
import { AccountNudge } from '../components/AccountNudge';
import { GameReview } from '../components/GameReview';
import { Kicked } from '../components/Kicked';
import { CopyLink, JoinedPlayers, useSessionInfo } from '../components/Lobby';
import { LeaderBadge } from '../show/LeaderBadge';
import { StartCountdown } from '../show/StartCountdown';

/** Keyed by code, so following a rematch to a new code gets a fresh socket and a fresh seat. */
export function Player() {
  const { code = '' } = useParams();
  return <PlayerScreen key={code} />;
}

/** Router state when a phone follows its game's rematch: the name to sit down under. */
interface FollowState {
  followName?: string;
}

function PlayerScreen() {
  const { code = '' } = useParams();
  const navigate = useNavigate();
  const followName = (useLocation().state as FollowState | null)?.followName;

  // Held in state, not read straight from localStorage: a claim or a host-side
  // merge hands this device a different token mid-session, and `useSession`
  // replays whatever is here on every reconnect.
  const [identity, setIdentity] = useState<StoredPlayer | null>(() => loadPlayer(code));
  const sessionInfo = useSessionInfo(code);

  // A remembered token rejoins silently — no name prompt on a phone reload.
  const autoJoin: JoinParams | undefined = useMemo(
    () =>
      identity ? { as: 'player', code, name: identity.name, token: identity.token } : undefined,
    [identity, code],
  );

  const {
    snapshot,
    connected,
    joined,
    joinError,
    toast,
    events,
    claimOffer,
    kicked,
    join,
    submit,
    send,
    notify,
    clearToast,
  } = useSession(autoJoin);
  useRecordResult(snapshot);

  const [name, setName] = useState(identity?.name ?? followName ?? '');
  const [joining, setJoining] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  /** Persist a token and re-arm the auto-join so reconnects use it too. */
  const adoptIdentity = useCallback(
    (token: string, playerName: string) => {
      const next = { token, name: playerName };
      savePlayer(code, next);
      setIdentity(next);
      // `<IdentityBar>` cannot watch `localStorage`.
      notePlayerChanged();
    },
    [code],
  );

  /**
   * The host merged this device's player into another. Our token is now dead, so take the
   * survivor's before the next reconnect uses it.
   */
  const merged = events.identityMerged;
  useEffect(() => {
    if (!merged?.payload) return;
    const { token, name: survivorName } = merged.payload as { token?: string; name?: string };
    if (token && survivorName) adoptIdentity(token, survivorName);
  }, [merged, adoptIdentity]);

  // Followed here from a finished game: sit down under the same name, no form. Once (a ref, so
  // StrictMode's re-run cannot seat twice); a refusal leaves the name form, already filled in.
  const followed = useRef(false);
  useEffect(() => {
    if (identity || !followName || followed.current) return;
    followed.current = true;
    void join({ as: 'player', code, name: followName }).then((ack) => {
      if (ack.ok && ack.token) adoptIdentity(ack.token, followName);
    });
  }, [identity, followName, code, join, adoptIdentity]);

  // The creator pressed Play Again: everyone seated here follows to the new lobby.
  const playAgainCode = snapshot?.core.playAgainCode ?? null;
  useEffect(() => {
    if (playAgainCode && identity) navigate(`/play/${playAgainCode}`, { state: { followName: identity.name } });
  }, [playAgainCode, identity, navigate]);

  /** The host let this phone back in: the same token, the same name. */
  const rejoin = useCallback(() => {
    if (identity) void join({ as: 'player', code, name: identity.name, token: identity.token });
  }, [identity, code, join]);

  async function submitName(event: React.FormEvent) {
    event.preventDefault();
    if (!name.trim()) return;
    setJoining(true);
    setFormError(null);

    const ack = await join({ as: 'player', code, name: name.trim(), token: identity?.token });
    setJoining(false);

    // A claim offer comes back through `claimOffer`, which renders the prompt.
    if (ack.claimOffer) return;
    if (!ack.ok) {
      setFormError(ack.error ?? 'Could not join.');
      return;
    }
    if (ack.token) adoptIdentity(ack.token, name.trim());
  }

  /** "Yes, that's me" — take over the offered participant and their score. */
  async function acceptClaim() {
    if (!claimOffer) return;
    setJoining(true);
    setFormError(null);

    const ack = await join({
      as: 'player',
      code,
      name: claimOffer.name,
      claimParticipantId: claimOffer.participantId,
    });
    setJoining(false);

    if (!ack.ok) {
      setFormError(ack.error ?? 'Could not rejoin.');
      return;
    }
    if (ack.token) adoptIdentity(ack.token, claimOffer.name);
  }

  /** "No, I'm someone else" — start fresh under the name they typed. */
  async function declineClaim() {
    if (!claimOffer) return;
    const wanted = name.trim() || claimOffer.name;
    setJoining(true);
    setFormError(null);

    const ack = await join({ as: 'player', code, name: wanted, forceNew: true });
    setJoining(false);

    if (!ack.ok) {
      setFormError(ack.error ?? 'Could not join.');
      return;
    }
    if (ack.token) adoptIdentity(ack.token, wanted);
  }

  if (kicked && identity) {
    return <Kicked code={code} token={identity.token} onAdmitted={rejoin} />;
  }

  if (claimOffer && !joined) {
    return (
      <div className="centered">
        <div className="card col">
          <h1>Welcome back?</h1>
          <p className="muted" style={{ marginTop: 0 }}>
            There's already a <strong>{claimOffer.name}</strong> in this game on{' '}
            <strong>{claimOffer.score}</strong> points, and they've dropped off. If that's you, pick
            up where you left off.
          </p>
          {formError ? <p className="error-text">{formError}</p> : null}
          <button
            className="btn btn-primary btn-lg"
            disabled={joining || !connected}
            onClick={acceptClaim}
          >
            {joining ? 'Rejoining…' : `Yes — I'm ${claimOffer.name}`}
          </button>
          <button className="btn" disabled={joining || !connected} onClick={declineClaim}>
            No, I'm someone else
          </button>
          <p className="faint" style={{ marginBottom: 0 }}>
            The host is told either way.
          </p>
        </div>
      </div>
    );
  }

  if (joinError && !joined) {
    return (
      <div className="centered">
        <div className="card col">
          <h1>Can’t join “{code}”</h1>
          <p className="error-text">{joinError}</p>
          <Link className="btn" to="/join">
            Try another code
          </Link>
        </div>
      </div>
    );
  }

  if (!joined || !snapshot) {
    return (
      <div className="centered">
        <form className="card col" onSubmit={submitName}>
          <h1>Join game {code.toUpperCase()}</h1>
          <div className="field">
            <label htmlFor="name">Your name</label>
            <input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={24}
              autoFocus
              autoComplete="given-name"
              placeholder="e.g. Andrew"
              style={{ fontSize: '1.3rem' }}
            />
          </div>
          {formError ? <p className="error-text">{formError}</p> : null}
          <button
            className="btn btn-primary btn-lg"
            type="submit"
            disabled={!name.trim() || joining || !connected}
          >
            {connected ? (joining ? 'Joining…' : "I'm in") : 'Connecting…'}
          </button>
        </form>
      </div>
    );
  }

  if (!snapshot.core.segment) {
    return (
      <>
        <Interstitial snapshot={snapshot} send={send} surface="player" connected={connected} />
        {snapshot.core.stage === 'results' ? (
          <>
            <div className="centered">
              <div className="card col" style={{ width: 'min(34rem, 100%)' }}>
                <GameReview code={code} token={loadPlayer(code)?.token ?? null} />
              </div>
            </div>
            <AccountNudge />
          </>
        ) : null}
        <Toast message={toast} onDismiss={clearToast} />
      </>
    );
  }

  // A Play Now game waits in the lobby for its creator's start, with nothing for this phone to do,
  // so the waiting room shows who is here and the link to bring more. (Other unhosted shows play
  // their first round from the lobby stage, so the stage alone does not say "waiting".)
  if (snapshot.core.stage === 'lobby' && sessionInfo.playNow) {
    return (
      <div className="centered">
        <div className="card col" style={{ width: 'min(34rem, 100%)' }}>
          <h1>{snapshot.core.title}</h1>
          <p className="muted" style={{ marginTop: 0 }}>
            You’re in. The game starts when whoever set it up presses start.
          </p>
          <CopyLink url={sessionInfo.joinUrl} />
          <JoinedPlayers snapshot={snapshot} />
        </div>
        <Toast message={toast} onDismiss={clearToast} />
      </div>
    );
  }

  const ui = uiFor(snapshot.core.segment.gameId);

  return (
    <>
      <LeaderBadge snapshot={snapshot} />
      {ui ? (
        <ui.player
          snapshot={snapshot}
          send={send}
          submit={submit}
          events={events}
          connected={connected}
          notify={notify}
          onLeave={() => {
            clearPlayer(code);
            notePlayerChanged();
            location.reload();
          }}
        />
      ) : (
        <div className="centered">
          <p className="muted">Waiting for the host to start a game…</p>
        </div>
      )}
      <StartCountdown snapshot={snapshot} />
      <Toast message={toast} onDismiss={clearToast} />
    </>
  );
}
